"use client"
import { useEffect, useRef, useState } from 'react'

import { useAnimationTuning } from '@/app/playbook/animation/animationTuningContext'
import { resolveLineMorphCssEasing } from '@/app/playbook/animation/animationTuningDefaults'
import { ChooseYourPathArrowLabel } from './ChooseYourPathArrowLabel'
import type { DiagramArrowStyle, LabelAnchor } from './chooseYourPathDiagramModel'

export type ChooseYourPathArrow = {
  from: { x: number, y: number }
  to: { x: number, y: number }
  label?: string
  /**
   * The point (% of width, px y) the label box pins itself to. Defaults to the
   * arrow midpoint when omitted. Separate from from/to so a stemmed arrow can
   * anchor its label on the stem rather than the full path's midpoint.
   */
  labelAt?: { xPct: number, y: number }
  /** Which corner/edge of the label box pins to `labelAt`. */
  labelAnchor?: LabelAnchor
  /** Label box width, as a percentage of the diagram width. Enables wrapping. */
  labelWidthPct?: number
  noHead?: boolean
  /** Per-arrow stroke override; fields fall back to the component-level stroke. */
  stroke?: DiagramArrowStyle
  /** Horizontal to destination x at start.y, then vertical to destination — arrowhead points along last segment. */
  bend?: 'hv'
}

type ChooseYourPathArrowsProps = {
  arrows: ChooseYourPathArrow[]
  height: number
  labelClassName?: string
  /** Per-design stroke overrides (see DiagramArrowStyle); omitted = stock. */
  stroke?: DiagramArrowStyle
}

const ARROW_HEAD_SIZE = 12
const STROKE_WIDTH = 2.25
// Gap between the arrow's anchor point and the side-hung label box.
const LABEL_GAP_PX = 6
// How much of the diagram must be on screen before drawIn arrows start.
const DRAW_IN_VISIBILITY_THRESHOLD = 0.35

export const ChooseYourPathArrows = ({
  arrows,
  height,
  labelClassName = 'text-[14px] italic leading-[16px]',
  stroke,
}: ChooseYourPathArrowsProps) => {
  // Per-field fallback chain: arrow.stroke -> component stroke -> stock.
  const resolveStroke = (arrow: ChooseYourPathArrow) => {
    const strokeWidth = arrow.stroke?.strokeWidth ?? stroke?.strokeWidth ?? STROKE_WIDTH
    const headSize = arrow.stroke?.headSize ?? stroke?.headSize ?? ARROW_HEAD_SIZE
    // Shafts only — heads stay solid unless dottedHeads opts them in.
    const shaftDasharray = arrow.stroke?.dasharray ?? stroke?.dasharray
    const strokeColor = arrow.stroke?.color ?? stroke?.color ?? 'currentColor'
    const headsSuppressed = arrow.stroke?.noHeads ?? stroke?.noHeads ?? false
    const dottedHeads = arrow.stroke?.dottedHeads ?? stroke?.dottedHeads ?? false
    const squareCaps = arrow.stroke?.squareCaps ?? stroke?.squareCaps ?? false
    const drawIn = arrow.stroke?.drawIn ?? stroke?.drawIn
    return {
      strokeWidth,
      headSize,
      shaftDasharray,
      strokeColor,
      headsSuppressed,
      headDasharray: dottedHeads ? shaftDasharray : undefined,
      linecap: squareCaps ? ('butt' as const) : ('round' as const),
      linejoin: squareCaps ? ('miter' as const) : ('round' as const),
      drawIn,
    }
  }
  const containerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)

  // Hovered-plan emphasis re-strokes arrows on the HUD's shared sync clock —
  // the same duration and S-curve the graph uses to reshape the red
  // loss-of-control overlay and morph its lines — so the diagram and the
  // graph react to a hover as one movement. (Shared const: used by both the
  // bent-polyline and straight-line render paths below.)
  const tuning = useAnimationTuning()
  const emphasisEasing = resolveLineMorphCssEasing(tuning.lineMorphEasing)
  const emphasisTransition =
    `stroke ${tuning.hudSyncTransitionMs}ms ${emphasisEasing}, ` +
    `stroke-width ${tuning.hudSyncTransitionMs}ms ${emphasisEasing}`

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    const updateWidth = () => setWidth(element.getBoundingClientRect().width)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  // drawIn arrows animate only once the diagram is actually on screen, and
  // not at all under prefers-reduced-motion (they render fully drawn).
  const hasDrawIn = arrows.some(arrow => (arrow.stroke?.drawIn ?? stroke?.drawIn) != null)
  const [revealed, setRevealed] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    if (!hasDrawIn) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const onChange = () => setReducedMotion(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [hasDrawIn])
  useEffect(() => {
    if (!hasDrawIn) return
    const element = containerRef.current
    if (!element) return
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setRevealed(true)
          io.disconnect()
        }
      },
      { threshold: DRAW_IN_VISIBILITY_THRESHOLD },
    )
    io.observe(element)
    return () => io.disconnect()
  }, [hasDrawIn])

  const pctToPx = (xPercent: number) => (xPercent / 100) * width

  // Snap an axis-aligned line's cross-axis coordinate so the stroke's EDGES
  // land on whole pixels (center at int + w/2) — %-derived fractional
  // positions otherwise anti-alias every vertical/horizontal line into a
  // blurry band. Diagonals are left alone; AA is correct there.
  const snapCenter = (coord: number, w: number) => Math.round(coord - w / 2) + w / 2
  const snapLine = (pts: { x1: number; y1: number; x2: number; y2: number }, w: number) => {
    if (pts.x1 === pts.x2) {
      const x = snapCenter(pts.x1, w)
      return { x1: x, y1: Math.round(pts.y1), x2: x, y2: Math.round(pts.y2) }
    }
    if (pts.y1 === pts.y2) {
      const y = snapCenter(pts.y1, w)
      return { x1: Math.round(pts.x1), y1: y, x2: Math.round(pts.x2), y2: y }
    }
    return pts
  }
  // 'hv' polyline (x1,y1)→(x2,y1)→(x2,y2): the horizontal leg rides y1, the
  // vertical leg rides x2 — snap each by the stroke width, ends to ints.
  const snapBend = (pts: { x1: number; y1: number; x2: number; y2: number }, w: number) => ({
    x1: Math.round(pts.x1),
    y1: snapCenter(pts.y1, w),
    x2: snapCenter(pts.x2, w),
    y2: Math.round(pts.y2),
  })

  // Where an arrow's label pins, in px: an explicit labelAt, else the arrow's
  // visual midpoint (bend-aware — the midpoint of the longer leg). Uses the
  // same snapped coordinates as the drawn line so the label tracks it exactly.
  const labelPinFor = (arrow: ChooseYourPathArrow) => {
    const { strokeWidth } = resolveStroke(arrow)
    const raw = {
      x1: pctToPx(arrow.from.x),
      y1: arrow.from.y,
      x2: pctToPx(arrow.to.x),
      y2: arrow.to.y,
    }
    const snap = arrow.bend === 'hv' ? snapBend : snapLine
    const { x1, y1, x2, y2 } = snap(raw, strokeWidth)
    const legHoriz = arrow.bend === 'hv' ? Math.abs(x2 - x1) : 0
    const legVert = arrow.bend === 'hv' ? Math.abs(y2 - y1) : 0
    const midX = arrow.bend === 'hv'
      ? (legHoriz > legVert ? (x1 + x2) / 2 : x2)
      : (x1 + x2) / 2
    const midY = arrow.bend === 'hv'
      ? (legHoriz > legVert ? y1 : (y1 + y2) / 2)
      : (y1 + y2) / 2
    return {
      x: arrow.labelAt ? pctToPx(arrow.labelAt.xPct) : midX,
      y: arrow.labelAt ? arrow.labelAt.y : midY,
    }
  }

  const arrowPathLength = (arrow: ChooseYourPathArrow) => {
    const dx = pctToPx(arrow.to.x) - pctToPx(arrow.from.x)
    const dy = arrow.to.y - arrow.from.y
    // 'hv' draws the horizontal then vertical leg, so its painted length is
    // the manhattan distance, not the hypot.
    return arrow.bend === 'hv' ? Math.abs(dx) + Math.abs(dy) : Math.hypot(dx, dy)
  }

  // Chain the drawIn arrows top-to-bottom: each segment starts sweeping the
  // moment the previous one lands, at a shared px/s speed.
  const drawInTimings = new Map<number, { delayMs: number; durationMs: number; lengthPx: number }>()
  if (width > 0 && hasDrawIn) {
    const items = arrows
      .map((arrow, index) => ({ arrow, index, drawIn: arrow.stroke?.drawIn ?? stroke?.drawIn }))
      .filter(item => item.drawIn != null)
      .sort((a, b) => a.arrow.from.y - b.arrow.from.y)
    let startMs = items.length ? (items[0].drawIn?.initialDelayMs ?? 400) : 0
    for (const item of items) {
      const lengthPx = arrowPathLength(item.arrow)
      const durationMs = (lengthPx / (item.drawIn?.pxPerSecond ?? 280)) * 1000
      drawInTimings.set(item.index, { delayMs: startMs, durationMs, lengthPx })
      startMs += durationMs
    }
  }

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none"
    >
      {hasDrawIn && (
        <style>{'@keyframes cypDrawIn { to { stroke-dashoffset: 0 } }'}</style>
      )}
      {width > 0 && (
        <svg width={width} height={height} className="overflow-visible">
          {arrows.map((arrow, index) => {
            const { strokeWidth, headSize, shaftDasharray, strokeColor, headsSuppressed, headDasharray, linecap, linejoin } = resolveStroke(arrow)
            const raw = {
              x1: pctToPx(arrow.from.x),
              y1: arrow.from.y,
              x2: pctToPx(arrow.to.x),
              y2: arrow.to.y,
            }
            const snap = arrow.bend === 'hv' ? snapBend : snapLine
            const { x1, y1, x2, y2 } = snap(raw, strokeWidth)
            const bendMidX = x2
            const bendMidY = y1
            const headAngle = arrow.bend === 'hv'
              ? Math.atan2(y2 - bendMidY, x2 - bendMidX)
              : Math.atan2(y2 - y1, x2 - x1)
            const headX1 = x2 - headSize * Math.cos(headAngle - Math.PI / 6)
            const headY1 = y2 - headSize * Math.sin(headAngle - Math.PI / 6)
            const headX2 = x2 - headSize * Math.cos(headAngle + Math.PI / 6)
            const headY2 = y2 - headSize * Math.sin(headAngle + Math.PI / 6)
            // Sweep-in state for this arrow (undefined = draws statically).
            // Under reduced motion the emphasized stroke renders fully drawn.
            const drawTiming = drawInTimings.get(index)
            const drawStyle = drawTiming && !reducedMotion
              ? {
                  strokeDasharray: drawTiming.lengthPx,
                  strokeDashoffset: drawTiming.lengthPx,
                  animation: revealed
                    ? `cypDrawIn ${drawTiming.durationMs}ms linear ${drawTiming.delayMs}ms forwards`
                    : 'none',
                }
              : undefined
            // Static underlay in the design's base stroke, so the path reads
            // as an ordinary quiet line until the sweep covers it.
            const underlay = drawTiming
              ? {
                  width: stroke?.strokeWidth ?? STROKE_WIDTH,
                  color: stroke?.color ?? 'currentColor',
                }
              : undefined
            // The underlay is thinner than the sweep, so it snaps to its own
            // stroke width (the sweep still fully covers it).
            const u = underlay ? snap(raw, underlay.width) : null

            return (
              <g key={index}>
                {arrow.bend === 'hv'
                  ? (
                    <>
                      {underlay && u && (
                        <polyline
                          points={`${u.x1},${u.y1} ${u.x2},${u.y1} ${u.x2},${u.y2}`}
                          stroke={underlay.color}
                          strokeWidth={underlay.width}
                          strokeLinecap={linecap}
                          strokeLinejoin={linejoin}
                          fill="none"
                        />
                      )}
                      <polyline
                        points={`${x1},${y1} ${bendMidX},${bendMidY} ${x2},${y2}`}
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={drawStyle ? undefined : shaftDasharray}
                        strokeLinecap={linecap}
                        strokeLinejoin={linejoin}
                        fill="none"
                        style={{ transition: emphasisTransition, ...drawStyle }}
                      />
                    </>
                    )
                  : (
                    <>
                      {underlay && u && (
                        <line
                          x1={u.x1}
                          y1={u.y1}
                          x2={u.x2}
                          y2={u.y2}
                          stroke={underlay.color}
                          strokeWidth={underlay.width}
                          strokeLinecap={linecap}
                        />
                      )}
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={strokeColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={drawStyle ? undefined : shaftDasharray}
                        strokeLinecap={linecap}
                        style={{ transition: emphasisTransition, ...drawStyle }}
                      />
                    </>
                    )}
                {!arrow.noHead && !headsSuppressed && (
                  <polyline
                    points={`${headX1},${headY1} ${x2},${y2} ${headX2},${headY2}`}
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    strokeDasharray={headDasharray}
                    strokeLinecap={linecap}
                    strokeLinejoin={linejoin}
                    fill="none"
                  />
                )}
              </g>
            )
          })}
        </svg>
      )}
      {/* Labels live OUTSIDE the svg, as plain absolutely-positioned HTML:
          Safari applies page zoom to foreignObject content on top of the SVG
          coordinate mapping, mispositioning it at any zoom other than 100%. */}
      {width > 0 && arrows.map((arrow, index) => {
        if (!arrow.label) return null
        const [hAnchor, vAnchor] = (arrow.labelAnchor ?? 'center-center').split('-')
        // Fraction of the box's own size that sits left of / above the
        // pin. The box self-sizes; we translate by these fractions so
        // the named corner lands on the pin without ever measuring or
        // guessing the text height.
        const hFrac = hAnchor === 'left' ? 0 : hAnchor === 'right' ? 1 : 0.5
        const vFrac = vAnchor === 'top' ? 0 : vAnchor === 'bottom' ? 1 : 0.5
        // Nudge the box off the line in the direction it hangs, so it
        // doesn't sit on top of the arrow. (No nudge when centered.)
        const gapX = hAnchor === 'right' ? -LABEL_GAP_PX : hAnchor === 'left' ? LABEL_GAP_PX : 0
        const textAlign = hAnchor === 'right' ? 'right' : hAnchor === 'left' ? 'left' : 'center'
        const pin = labelPinFor(arrow)
        return (
          // Zero-size box pinned at the anchor point; the inner div carries
          // width + a transform that aligns the chosen corner to the pin.
          // Height comes from the wrapped text, so '*-center' is always
          // genuinely centered.
          <div
            key={index}
            className="absolute"
            style={{ left: pin.x + gapX, top: pin.y }}
          >
            <ChooseYourPathArrowLabel
              label={arrow.label}
              labelClassName={labelClassName}
              width={width}
              labelWidthPct={arrow.labelWidthPct}
              hFrac={hFrac}
              vFrac={vFrac}
              textAlign={textAlign}
            />
          </div>
        )
      })}
    </div>
  )
}
